'use client';

import {
  createContext,
  useContext,
  useCallback,
  useSyncExternalStore,
  type ReactNode,
} from 'react';

import { track } from '@/lib/telemetry';

interface PrivacyContextType {
  hideValues: boolean;
  toggleHideValues: () => void;
}

const PrivacyContext = createContext<PrivacyContextType | null>(null);

const STORAGE_KEY = 'finance-control:hide-values';

let listeners: Array<() => void> = [];

function emitChange() {
  for (const listener of listeners) {
    listener();
  }
}

function subscribe(listener: () => void) {
  listeners = [...listeners, listener];
  return () => {
    listeners = listeners.filter((l) => l !== listener);
  };
}

function getSnapshot(): boolean {
  return localStorage.getItem(STORAGE_KEY) === 'true';
}

function getServerSnapshot(): boolean {
  return false;
}

export function PrivacyProvider({ children }: { children: ReactNode }) {
  const hideValues = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const toggleHideValues = useCallback(() => {
    track('action.settings.toggle_privacy', { hidden: !hideValues });
    localStorage.setItem(STORAGE_KEY, String(!hideValues));
    emitChange();
  }, [hideValues]);

  return (
    <PrivacyContext.Provider value={{ hideValues, toggleHideValues }}>
      {children}
    </PrivacyContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function usePrivacy() {
  const context = useContext(PrivacyContext);
  if (!context) {
    throw new Error('usePrivacy must be used within a PrivacyProvider');
  }
  return context;
}
